import { ref, computed } from 'vue';
import Swal from 'sweetalert2';
import { usePlatformAPI } from '@/composables/api/usePlatformAPI';

/**
 * Composable que gestiona el estado de inscripción del usuario en una plataforma,
 * la postulación y el acceso al enlace de la plataforma.
 *
 * @param {string|number} platformId - ID de la plataforma (route param)
 */
export function useUserRegistration(platformId) {
  const { getUserPlatformStatus, saveUserPlatform, getUserPlatformAccess } = usePlatformAPI();

  const idTypeStatus = ref(0);
  const idTypeStatusPayment = ref(0);
  const idUserPlatform = ref(null);

  // ===== Computed =====

  const alreadyRegistered = computed(() => {
    return idTypeStatus.value !== 0 && idTypeStatus.value !== 2;
  });

  const isRejected = computed(() => idTypeStatus.value == 2);

  const canPay = computed(() => {
    return idTypeStatus.value == 3 && idTypeStatusPayment.value == 1;
  });

  const isFullyEnrolled = computed(() => {
    return idTypeStatus.value == 3 && idTypeStatusPayment.value == 3;
  });

  const registrationStatusText = computed(() => {
    if (!idTypeStatus.value || idTypeStatus.value === 0) return '';

    if (idTypeStatus.value == 1) return 'Postulación en revisión';
    if (idTypeStatus.value == 2) return 'Documentos rechazados';

    if (idTypeStatus.value == 3) {
      if (idTypeStatusPayment.value == 1) return 'Pendiente de pago';
      if (idTypeStatusPayment.value == 2) return 'Pago en revisión';
      if (idTypeStatusPayment.value == 3) return 'Inscrito';
    }

    return '';
  });

  const registrationStatusClass = computed(() => {
    if (idTypeStatus.value == 1) return 'bg-warning text-dark';
    if (idTypeStatus.value == 2) return 'bg-danger text-white';

    if (idTypeStatus.value == 3) {
      if (idTypeStatusPayment.value == 1) return 'bg-warning text-dark';
      if (idTypeStatusPayment.value == 2) return 'bg-info text-white';
      if (idTypeStatusPayment.value == 3) return 'bg-success text-white';
    }

    return 'bg-secondary text-white';
  });

  // ===== Methods =====

  /**
   * Carga el estado de inscripción del usuario para la plataforma actual.
   * Actualiza idTypeStatus, idTypeStatusPayment, idUserPlatform.
   */
  const loadStatus = async () => {
    const response = await getUserPlatformStatus(platformId);
    if (response?.code === 200 && response.data?.length > 0) {
      const record = response.data[0];
      idTypeStatus.value = record.id_type_status;
      idTypeStatusPayment.value = record.id_type_status_payment;
      idUserPlatform.value = record.id;

      return {
        idUserPlatform: record.id,
        idPlatformOffer: record.id_platform_offer || null,
      };
    } else {
      idTypeStatus.value = 0;
    }
    return null;
  };

  /**
   * Ejecuta la postulación del usuario a la plataforma.
   * Maneja el flujo de confirmación con SweetAlert2.
   *
   * @param {Object} platform - Datos de la plataforma
   * @param {string|number} offerId - ID de la oferta seleccionada
   * @param {number} autoApproved - 1 si la plataforma auto-aprueba
   * @returns {Promise<{ success: boolean, redirect?: string }>}
   */
  const postulateMe = async (platform, offerId, autoApproved) => {
    if (alreadyRegistered.value && !isRejected.value) {
      Swal.fire({
        icon: 'info',
        title: 'Ya estás inscrito',
        text: 'Ya tienes una postulación activa para este curso.',
        confirmButtonText: 'Entendido',
      });
      return { success: false };
    }

    if (autoApproved == 1) {
      return await _postulateAutoApproved(platform);
    }

    return await _postulateWithConfirmation(platform, offerId);
  };

  const _postulateWithConfirmation = async (platform, offerId) => {
    const result = await Swal.fire({
      title: '¡Atención!',
      text: `¿Estás seguro de postularte a ${platform.name}?`,
      icon: 'question',
      reverseButtons: true,
      showCancelButton: true,
      confirmButtonColor: '#B49B57',
      cancelButtonColor: '#9F9F9F',
      cancelButtonText: 'Cancelar',
      confirmButtonText: 'Si, continuar',
    });

    if (!result.value) return { success: false };

    let data;
    if (isRejected.value && idUserPlatform.value) {
      data = {
        id: idUserPlatform.value,
        id_user: undefined,
        id_platform: platform.id,
        id_platform_offer: offerId,
        id_type_status: 1,
      };
    } else {
      data = {
        id_platform: platform.id,
        id_platform_offer: offerId,
      };
    }

    const response = await saveUserPlatform(data);

    if (response?.code === 200 && response?.status === 'OK') {
      await Swal.fire({
        title: 'Postulación enviada correctamente',
        text: 'Ahora debes esperar a que respondan tu solicitud',
        icon: 'success',
        showConfirmButton: true,
      });
      return { success: true, redirect: 'Overview' };
    } else {
      Swal.fire({
        title: 'No se pudo realizar la postulación',
        text: response?.message || 'Ocurrió un error al procesar tu solicitud',
        icon: 'error',
        confirmButtonText: 'Entendido',
      });
      return { success: false };
    }
  };

  const _postulateAutoApproved = async (platform) => {
    const data = {
      id_platform: platform.id,
      id_type_status: 3,
      id_type_status_payment: 3,
    };

    const response = await saveUserPlatform(data);

    if (response?.code === 200 && response?.status === 'OK') {
      await Swal.fire({
        title: '¡Bienvenido al curso!',
        text: 'Ahora puedes acceder al curso',
        icon: 'success',
        showConfirmButton: false,
        timer: 1000,
      });
      return { success: true, redirect: 'Overview' };
    } else {
      Swal.fire({
        title: 'No se pudo realizar la inscripción',
        text: response?.message || 'Ocurrió un error al procesar tu solicitud',
        icon: 'error',
        confirmButtonText: 'Entendido',
      });
      return { success: false };
    }
  };

  /**
   * Solicita y abre el enlace de acceso a la plataforma.
   * Requiere que idUserPlatform esté establecido.
   */
  const handleAccessPlatform = async () => {
    if (!idUserPlatform.value) {
      Swal.fire({
        title: 'Error',
        text: 'No se encontró la información de tu inscripción',
        icon: 'error',
        confirmButtonText: 'Entendido',
      });
      return;
    }

    Swal.fire({
      title: 'Obteniendo acceso...',
      text: 'Por favor espera',
      icon: 'info',
      showConfirmButton: false,
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    const response = await getUserPlatformAccess(idUserPlatform.value);
    Swal.close();

    if (response?.code === 200 && response.data) {
      if (response.data.status !== 'AVAILABLE') {
        Swal.fire({
          title: 'Acceso no disponible',
          text: 'Aún no has sido aprobado para acceder a la plataforma',
          icon: 'warning',
          confirmButtonText: 'Entendido',
        });
      } else if (response.data.url) {
        window.open(response.data.url, '_blank');
      } else {
        Swal.fire({
          title: 'Error',
          text: 'No se pudo obtener el enlace de acceso',
          icon: 'error',
          confirmButtonText: 'Entendido',
        });
      }
    } else {
      Swal.fire({
        title: 'Error',
        text: 'Ocurrió un error al obtener el enlace de acceso',
        icon: 'error',
        confirmButtonText: 'Entendido',
      });
    }
  };

  return {
    idTypeStatus,
    idTypeStatusPayment,
    idUserPlatform,
    alreadyRegistered,
    isRejected,
    canPay,
    isFullyEnrolled,
    registrationStatusText,
    registrationStatusClass,
    loadStatus,
    postulateMe,
    handleAccessPlatform,
  };
}
